import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Divider, List, ListItem, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { ReactElement, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IOrganization, IUser } from 'common/src/types/interfaces';
import {
  ICreateOrganizationDto,
  ICreateOrganizationWithInitialUsersDto,
  IUpdateOrganizationDto,
} from 'common/src/types/organization.dtos';
import {
  requiredEsContactFields,
  requiredEsContactFieldsErrMsg,
} from 'common/src/validators/esValidationFields';

import { QUERY_KEYS } from 'portal/src/api/constants';
import {
  addOrganization,
  updateOrganization,
} from 'portal/src/api/OrganizationsService';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import FormViewFieldLayout from 'portal/src/components/common/forms/FormViewFieldLayout';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { getEsContactFields } from './OrganizationEsContactForm.fields';
import {
  defaultValues,
  getOrganizationFields,
  OrganizationFormFields as OrganizationFormValues,
} from './OrganizationForm.fields';
import PasswordRuleOverridesForm from './PasswordRuleOverridesForm';

import Users from '../../Users';
import InitialUsers from '../User/InitialUsers';

const PrefixHints = ({ hintItems }: { hintItems: string[] }): ReactElement => (
  <List dense sx={{ typography: 'caption' }}>
    {hintItems.map((item, idx) => (
      <ListItem key={idx}>
        <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
        {item}
      </ListItem>
    ))}
  </List>
);

const OrganizationForm = ({
  entity = {},
  handleBackAction,
  isSysAdminView,
}: {
  entity?: Partial<IOrganization>;
  isSysAdminView: boolean;
  handleBackAction?: () => void;
}) => {
  const {
    hasReadOnlyAccess,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasSystemAdminAccess,
    refreshUser,
  } = useAuth();
  const snackbarHandler = useSnackbarHandler();
  const queryClient = useQueryClient();
  const isAdmin = hasAdminAccess();
  const isSysAdmin = hasSystemAdminAccess();
  const isClientSysAdmin = hasClientSystemAdminAccess();
  const isReadOnly = hasReadOnlyAccess();

  const [initialUsers, setInitialUsers] = useState<Partial<IUser>[]>([]);

  const isDeactivated = entity.id && !entity.isActive;
  const organizationFormFieldsConfig = getOrganizationFields(
    !!entity.id,
    isSysAdmin,
    isClientSysAdmin,
  );

  const {
    control,
    formState: { dirtyFields, isDirty },
    handleSubmit,
    reset,
    ...formControl
  } = useForm({ defaultValues: defaultValues(entity) });
  const { getSnackbarApiErrorMessage } = useFormControlError(formControl);
  const esContactFieldsToWatch = requiredEsContactFields.map((field) => {
    if (field === 'esContactState') {
      return 'state';
    }

    if (field === 'esContactPostalCode') {
      return 'postalCode';
    }

    return field;
  }) as (keyof OrganizationFormValues)[];

  const esContactFields = useWatch({
    control,
    name: esContactFieldsToWatch,
  });

  const esContactFieldsConfig = getEsContactFields(
    esContactFields.some((field) => !!field),
  );

  const { mutate: mutateOrg, isPending } = useMutation({
    mutationFn: async (formValues: OrganizationFormValues) => {
      const payload = prepareFormPayload(
        {
          ...formValues,
          fiscalYearStartMonth:
            formValues.fiscalYearStartMonth?.getMonth() ?? 6,
        },
        {
          ...dirtyFields,
          externalAccountIds: !isEqual(
            entity.externalAccountIds,
            formValues.externalAccountIds,
          ),
          products: !isEqual(entity.products, formValues.products),
          productFeatures: !isEqual(
            entity.productFeatures,
            formValues.productFeatures,
          ),
          passwordRuleOverrides: !isEqual(
            entity.passwordRuleOverrides,
            formValues.passwordRuleOverrides,
          ),
        },
      );

      const {
        postalCode: esContactPostalCode,
        state: esContactState,
        ...restValues
      } = payload;

      const finalPayload = {
        ...restValues,
        esContactPostalCode,
        esContactState,
      } as ICreateOrganizationDto | IUpdateOrganizationDto;

      if (entity.id) {
        await updateOrganization(entity.id, finalPayload);
        await refreshUser();
        if (!isSysAdminView) {
          reset(formValues);
        }
        snackbarHandler.success(MESSAGES.ORGANIZATION.UPDATED);
      } else {
        await addOrganization({
          organization: finalPayload,
          initialUsers,
        } as ICreateOrganizationWithInitialUsersDto);
        snackbarHandler.success(MESSAGES.ORGANIZATION.CREATED);
      }

      if (isSysAdminView) {
        handleBackAction?.();
      }
    },
    onError: (err: unknown) => {
      snackbarHandler.error(getSnackbarApiErrorMessage(err));
    },
  });

  const resetForm = async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.organizations, entity.id],
    });
    reset(defaultValues(entity));
  };

  return (
    <FormContainer
      beforeMainFields={
        !entity.id && (
          <>
            <Typography variant="body2">
              The Organization ID prefix must:
            </Typography>
            <PrefixHints
              hintItems={[
                'Be at least 2 characters in length',
                'Only contain alphanumeric characters, spaces, and underscores',
                'Include the key word or words that would uniquely identify the organization',
                'Exclude generic words such as "City of", "Town of", "ISD", "School District", "SAU", "University"',
              ]}
            />
            <Typography variant="body2">Examples:</Typography>
            <PrefixHints
              hintItems={[
                'Round Rock ISD → Round Rock',
                'Portsmouth SAU 52 → Portsmouth',
                'City of Cupertino → Cupertino',
                'City of West Palm Beach → West Palm Beach',
              ]}
            />
          </>
        )
      }
      control={control}
      isDirty={isDirty || (!entity.id && initialUsers.length > 0)}
      handleBackAction={isSysAdminView ? handleBackAction : undefined}
      handleSubmit={handleSubmit((data) => mutateOrg(data))}
      header="Organization Settings"
      isDeactivated={!!isDeactivated}
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={organizationFormFieldsConfig}
      resetForm={isSysAdminView ? undefined : resetForm}
      afterMainFields={
        <>
          <Divider sx={{ mt: 2, mb: 1 }}>
            ENERGY STAR Contact Information
          </Divider>
          <Typography variant="body2" gutterBottom>
            This information will be used to connect the organization's ENERGY
            STAR account.
            <ListItem sx={{ typography: 'caption' }}>
              <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
              {requiredEsContactFieldsErrMsg}
            </ListItem>
          </Typography>
          <FormViewFieldLayout
            control={control}
            mainFieldsConfig={esContactFieldsConfig}
            isDeactivated={isDeactivated}
            isReadOnly={isReadOnly}
          />
          {isClientSysAdmin && (
            <>
              <Divider sx={{ mt: 2, mb: 1 }}>Password Rule Overrides</Divider>
              <PasswordRuleOverridesForm control={control} />
            </>
          )}
          {!entity.id ? (
            <>
              <Divider sx={{ mt: 2, mb: 1 }}>Initial Users</Divider>
              <InitialUsers
                onDataChange={setInitialUsers}
                users={initialUsers}
              />
            </>
          ) : (
            isAdmin && (
              <>
                <Divider sx={{ mt: 2, mb: 1 }}>Organization Users</Divider>
                <Users
                  routeFragment={NESTED_ROUTES.USERS}
                  organizationId={isSysAdminView ? entity?.id : undefined}
                />
              </>
            )
          )}
        </>
      }
    />
  );
};

export default OrganizationForm;
